import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { AppointmentRecord } from "~/api/API";
import { resolveFileOwnership } from "~/api/queries/resolveFileOwnership";
import {
  appointmentFilesKeys,
  FileDisplayData,
} from "~/api/queries/useAppointmentFiles";
import { useLinkedAppointmentFiles } from "~/api/queries/useLinkedAppointmentFiles";

interface UseResolvedLinkedFilesOptions {
  /**
   * Transform/filter the resolved files for this consumer (e.g. exclude billing
   * receipts). Applied after ownership resolution, mirroring the `select` option
   * on useAppointmentFiles.
   */
  select?: (files: FileDisplayData[]) => FileDisplayData[];
}

interface UseResolvedLinkedFilesResult {
  /** Deduplicated files across the linked family, with ownership-based isReadOnly */
  files: FileDisplayData[];
  /** Whether data is still loading */
  isLoading: boolean;
  /** Error if any query failed */
  error: Error | null;
  /** Refetch files for every appointment in the linked family */
  refetch: () => Promise<void>;
}

const EMPTY_FILES: FileDisplayData[] = [];

/**
 * Reads files for an appointment's full reschedule/linked family (via the
 * originalAppointmentID GSI) and resolves ownership across it with
 * `resolveFileOwnership`, returning a deduplicated flat list whose `isReadOnly`
 * / `referenceStatus` are attributed relative to the current appointment.
 *
 * Pass `undefined` to disable (no fetch) — lets a caller gate this on a flag
 * without breaking the Rules of Hooks.
 *
 * The read path inside `useLinkedAppointmentFiles` is itself flag-selected
 * (junction vs `UploadedFile.bySourceAppointment` GSI) by
 * `PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT`; this hook is agnostic to which.
 */
export function useResolvedLinkedFiles(
  appointment: AppointmentRecord | null | undefined,
  options: UseResolvedLinkedFilesOptions = {},
): UseResolvedLinkedFilesResult {
  const { select } = options;
  const queryClient = useQueryClient();

  const { filesByAppointment, appointmentMap, isLoading, error } =
    useLinkedAppointmentFiles(appointment);

  const files = useMemo(() => {
    if (!appointment || isLoading || filesByAppointment.size === 0) {
      return EMPTY_FILES;
    }

    const resolved = resolveFileOwnership({
      filesByAppointment,
      appointmentMap,
      currentAppointmentId: appointment.id,
    });

    return select ? select(resolved) : resolved;
  }, [appointment, isLoading, filesByAppointment, appointmentMap, select]);

  return {
    files,
    isLoading,
    error,
    refetch: async () => {
      const invalidations = Array.from(appointmentMap.keys()).map((id) =>
        queryClient.invalidateQueries({
          queryKey: appointmentFilesKeys.detail(id),
        }),
      );
      await Promise.all(invalidations);
    },
  };
}
